import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { MOBILE_MESSAGING_SERVICES_FIELDS } from '../../common/fields';
import Loader from '../../../Controls/Loader/Loader';

import styles from './MobileMessagingServices.module.css';
import { httpPost, isUserPermitted } from '../../../utils';
import Button from '../../../sf-components/Button/Button';
import ButtonStyles from '../../../sf-components/Button/ButtonStyles';
import Icons from '../../../sf-components/Icon/Icons';
import Label from '../../../sf-components/Label/Label';
import TextInput from '../../../sf-components/TextInput/TextInput';
import * as actions from '../../containers/Licensing/licensingActions';
import { HTTP_OPTIONS, ROLES } from '@silverfort-nodelibs/common/utils/consts';

const { ADMIN_CONSOLE_SENDERS, ADMIN_CONSOLE_RECEIVERS } = MOBILE_MESSAGING_SERVICES_FIELDS;

const REFRESH_CONNECTION_STRINGS_TIMEOUT = 1000 * 60; // one minute

const MobileMessagingServices = ({ fields, dispatch }) => {
    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);
    const adminConsoleSenders = fields[ADMIN_CONSOLE_SENDERS.ID];
    const adminConsoleReceivers = fields[ADMIN_CONSOLE_RECEIVERS.ID];
    const nodeIds = Object.keys(fields).filter(
        (fId) => fId !== ADMIN_CONSOLE_SENDERS.ID && fId !== ADMIN_CONSOLE_RECEIVERS.ID
    );

    const renderConnectionStrings = (connectionStringsField) => {
        if (connectionStringsField.loading) {
            return (
                <div className={styles.loadingContainer}>
                    <Loader extraStyles={{ borderColor: '#e5ebf1', borderTopColor: '#118ede' }} />
                    <div>Refreshing nodes connection strings...</div>
                </div>
            );
        }

        return (
            <div key={connectionStringsField.id} className={`pt-6`}>
                <div className={'text-xs font-sf text-sf-gray-500 '}> {connectionStringsField.name} </div>
                {connectionStringsField.value.length === 0 && (
                    <div className={styles.csContainer}>No connection strings</div>
                )}
                {connectionStringsField.value.map((cs, index) => (
                    <div key={`${cs.id}_${index}`} className={`${styles.csContainer} pt-4`}>
                        <Label text={`Connection String ${index + 1}:`}></Label>
                        <TextInput
                            {...cs[MOBILE_MESSAGING_SERVICES_FIELDS.CONNECTION_STRING.ID]}
                            rows={3}
                            disabled
                            multiline
                            className="w-[490px]"
                        />
                    </div>
                ))}
                {connectionStringsField.error && <div className={styles.errorText}>{connectionStringsField.error}</div>}
            </div>
        );
    };

    const refreshConnectionStrings = useCallback(async () => {
        dispatch({
            type: actions.REFRESH_CONNECTION_STRINGS_SENT
        });
        let connectionStrings = {};
        let error = null;
        try {
            const { data } = await httpPost('/Settings/RefreshConnectionStrings', undefined, {
                [HTTP_OPTIONS.REQ_OPTIONS]: { timeout: REFRESH_CONNECTION_STRINGS_TIMEOUT }
            });
            connectionStrings = data.connectionStrings;
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 500) {
                error = err.response.data.result || 'Failed to refresh nodes connection strings';
            }
        }

        dispatch({
            type: actions.UPDATE_NODES_CS,
            payload: { connectionStrings, error }
        });
    }, []);

    const nodesContent = nodeIds.map((nodeId) => renderConnectionStrings(fields[nodeId]));
    return (
        <div className={'py-4'}>
            <Button
                buttonStyle={ButtonStyles.Primary}
                text={'Refresh Connection Strings'}
                iconLeft={Icons.Refresh}
                onClick={refreshConnectionStrings}
                disabled={!isEditor}
            />
            {adminConsoleSenders && renderConnectionStrings(adminConsoleSenders)}
            {adminConsoleReceivers && renderConnectionStrings(adminConsoleReceivers)}
            {nodesContent}
        </div>
    );
};

export default React.memo(MobileMessagingServices);
